// Content model for the Seller Account Management hub page and all marketplace
// subpages. Every marketplace object carries the SAME set of fully-populated
// sections so that no section on any page is ever rendered blank.

// ---------------------------------------------------------------------------
// Shared content (reused across the hub and every marketplace subpage)
// ---------------------------------------------------------------------------

export const companyName = "K2 E-Commerce Solutions";

export const contact = {
  phones: ["+91 8077664242", "+91 8057615105"],
  // Used by the "Get Started" CTA (tel: link).
  phoneTel: "tel:+918077664242",
  // Used by the "Enquire Now" CTA — same WhatsApp link as the homepage.
  whatsapp: "https://wa.link/susy6s",
  email: "info@k2ecommercesolution.com",
  address:
    "SR-4 Phase 1, Transport Nagar, Chandrabani, Dehradun, Sewla Khurd, Uttarakhand 248001",
};

export const stats = [
  { value: "40K+", label: "Served Clients" },
  { value: "10+", label: "Years Experience" },
  { value: "10L+", label: "Daily Orders" },
  { value: "20Cr+", label: "Daily Revenue" },
];

export const whyChooseUs = [
  {
    title: "10+ Years Experience",
    desc: "A decade of hands-on marketplace expertise across India's biggest e-commerce platforms, refined campaign after campaign.",
  },
  {
    title: "Fantastic Results",
    desc: "A demonstrated track record of moving listings to the top of search, lifting conversions and growing month-on-month revenue.",
  },
  {
    title: "Marketplace Experts",
    desc: "A dedicated, platform-trained team that lives inside seller dashboards every day and knows each marketplace's rules cold.",
  },
  {
    title: "Enhanced Brand Visibility",
    desc: "Catalogue, content and advertising work together to make your brand discoverable and memorable to high-intent shoppers.",
  },
  {
    title: "Lowest ACoS Maintained",
    desc: "Tight bid management and continuous keyword refinement keep your advertising cost of sale low while sales keep climbing.",
  },
  {
    title: "End-to-End Handling",
    desc: "From onboarding and listing to ads, inventory, payments and support — we manage the entire account so you can focus on product.",
  },
];

// Generic core-service template used to GUARANTEE a complete six-card services
// section even where a marketplace does not override it. Marketplaces below may
// supply their own `coreServices` to tailor the copy.
const genericCoreServices = (m) => [
  {
    title: "Account Setup & Onboarding",
    desc: `We handle the entire ${m} seller account setup — registration, document submission, category approvals and verification — so you go live correctly the first time.`,
  },
  {
    title: "Product Listing & Cataloguing",
    desc: `Keyword-rich titles, persuasive descriptions, clean images and complete specifications, built to ${m}'s catalogue standards for maximum discoverability.`,
  },
  {
    title: "Storefront Creation & Design",
    desc: `A professional ${m} storefront that reflects your brand identity, organises products into collections and turns browsers into buyers.`,
  },
  {
    title: "Optimization for Increased Sales",
    desc: `A full performance audit of your store followed by pricing, content and conversion-rate improvements aligned with ${m}'s ranking algorithm.`,
  },
  {
    title: "Promotion & Advertising",
    desc: `Sponsored ads, deals, flash sales and coupon campaigns managed for maximum ROI and the lowest possible advertising cost of sale.`,
  },
  {
    title: "Inventory Management",
    desc: `We monitor stock levels, trigger timely replenishment and prevent both stockouts and overstocking to protect your account health and profit.`,
  },
];

const genericListingOptimization = (m) => [
  {
    title: "Bulk & Single Listing",
    desc: "Precise attention to product categorisation, whether we are uploading one hero SKU or thousands of variants in bulk.",
  },
  {
    title: "Complete Cataloguing Support",
    desc: "Full management of SKU IDs, brands, images, URLs and descriptions so every attribute is accurate and compliant.",
  },
  {
    title: "Keyword-Optimized Listings",
    desc: "Research-driven keywords with high search potential woven naturally into titles, bullets and back-end fields.",
  },
  {
    title: "Promotional Listing Boost",
    desc: "Listings primed for sale events and advertising so they convert the extra traffic instead of wasting it.",
  },
  {
    title: "Listing Cleanup & Compliance",
    desc: `Removal of inactive inventory and quick resolution of ${m} policy and quality flags that suppress visibility.`,
  },
  {
    title: "High-Ranking Listings",
    desc: "Continuous on-page tuning that earns and holds top search positions for your most important products.",
  },
];

const genericAdvertising = (m) => [
  {
    title: "Cost-per-Click Campaigns",
    desc: "Targeted CPC ads that reach high-intent buyers exactly when they are searching to purchase.",
  },
  {
    title: "Performance-Driven Management",
    desc: "Daily bid optimization and keyword refinement to push ROI up and advertising cost of sale down.",
  },
  {
    title: "Campaign Monitoring",
    desc: "Continuous tracking of placements, share of voice and competitor moves so your ads stay competitive.",
  },
  {
    title: "ROI-Focused Product Ads",
    desc: "Smart strategies that put spend behind your best-performing, best-margin products.",
  },
  {
    title: "Sponsored Brand Visibility",
    desc: "Brand-level placements that build recall and capture category demand at the top of results.",
  },
  {
    title: `Full ${m} Ad Management`,
    desc: "An all-inclusive program covering setup, creative, bidding, peak-season events and reporting.",
  },
];

const genericInventory = () => [
  {
    title: "Complete Stock Support",
    desc: "We prevent stockouts, overstocking, storage penalties and fulfilment delays before they hurt your ranking.",
  },
  {
    title: "Fulfilment Operations",
    desc: "Organised storage, accurate tracking and efficient shipping across fulfilment and self-ship models.",
  },
  {
    title: "Real-Time Warehouse Monitoring",
    desc: "Continuous oversight of stock levels across every facility your products are stored in.",
  },
  {
    title: "Stock Refill Alerts",
    desc: "Immediate notifications and clear guidance on when and how much to replenish.",
  },
  {
    title: "Ageing Stock Prevention",
    desc: "Active monitoring to avoid long-term storage fees and holding charges on slow movers.",
  },
  {
    title: "Clearance & Procurement Strategy",
    desc: "Promotional clearance for slow stock plus data-driven recommendations on what to buy next.",
  },
];

const genericPricing = (m) => [
  {
    title: "Perfect Pricing Support",
    desc: "Competitive yet profitable pricing guidance that protects your margin while staying in the buy box conversation.",
  },
  {
    title: "Know Your Costs",
    desc: `Clarity on every ${m} fee, commission and shipping charge before you set a price.`,
  },
  {
    title: "Commission Calculator",
    desc: "We map category-wise commissions so you always know your true take-home on each sale.",
  },
  {
    title: "Category-Based Rates",
    desc: "Understanding the varied rate structures across categories so pricing decisions are never guesswork.",
  },
  {
    title: "Fee Breakdown Transparency",
    desc: "A complete, itemised view of referral, closing, shipping and storage fees.",
  },
  {
    title: "Profit Through Volume",
    desc: "A strategic, volume-aware approach that grows total profit, not just per-unit margin.",
  },
];

// ---------------------------------------------------------------------------
// Per-marketplace content
// ---------------------------------------------------------------------------

export const marketplaces = {
  amazon: {
    slug: "amazon",
    name: "Amazon",
    icon: "amazon",
    tagline:
      "Complete Amazon account management — listing, cataloguing, advertising and full compliance — to grow you faster and smarter on the world's biggest marketplace.",
    hero: `${companyName} offers complete Amazon seller account management services, including expert product listing, catalogue management and result-driven advertising. Whether you are a brand-new seller or scaling an established store, our tailored solutions boost product visibility, increase conversions and ensure full Amazon compliance. With a dedicated team and end-to-end support, we help you grow on the world's biggest marketplace.`,
    intro: [
      `In a marketplace where hundreds of sellers offer the same products, visibility is the single biggest differentiator. Being found in searches on Amazon and Google is what separates a store that grows from one that stalls — and that is exactly where expert Amazon catalogue services and account management make all the difference.`,
      `${companyName} is among India's leading Amazon account management providers. Choosing us means gaining more than a service vendor; you gain a strategic partner committed to your growth, navigating the complexities of the online marketplace alongside you.`,
    ],
    pillars: [
      {
        title: "Full Amazon Account Management",
        desc: "Day-to-day management of your Amazon seller account — health metrics, cases, policy compliance, payments and growth planning — all handled for you.",
      },
      {
        title: "Product Listing & Cataloguing",
        desc: "Rich, keyword-optimized listing pages that present every detail of your product clearly and rank for the searches that matter.",
      },
      {
        title: "Advertising & Marketing",
        desc: "Sponsored Products, Sponsored Brands and Sponsored Display campaigns plus deals and coupons that increase sales while controlling ACoS.",
      },
      {
        title: "Full Service & Support",
        desc: "Our relationship does not run nine-to-five. We deliver real-time support and stay with your account long after launch.",
      },
    ],
    coreServices: [
      {
        title: "Seller Central Account Setup",
        desc: "End-to-end Amazon seller registration, brand and GST onboarding, category approvals and Seller Central configuration done right the first time.",
      },
      {
        title: "Product Listing & A+ Content",
        desc: "Keyword-rich titles, scannable bullet points, enhanced A+ brand content and clean imagery that lift both ranking and conversion.",
      },
      {
        title: "Brand Store Design",
        desc: "A custom Amazon Brand Store that showcases your full catalogue, tells your brand story and gives ads a premium destination.",
      },
      {
        title: "Optimization for Increased Sales",
        desc: "Continuous tuning of price, content, images and reviews in line with the A9 ranking algorithm to compound your sales.",
      },
      {
        title: "Advertising & Promotions",
        desc: "Sponsored ad management, Lightning Deals, coupons and Prime-event planning engineered for maximum return on ad spend.",
      },
      {
        title: "Inventory & FBA Management",
        desc: "FBA shipment planning, stock-level monitoring and restock alerts that protect your IPI score and avoid long-term storage fees.",
      },
    ],
    listingOptimization: genericListingOptimization("Amazon"),
    advertising: [
      {
        title: "Sponsored Products",
        desc: "Keyword- and ASIN-targeted ads that place your products in front of buyers at the moment of purchase intent.",
      },
      {
        title: "Sponsored Brands",
        desc: "Headline and brand-logo placements that build recall and drive shoppers to your Brand Store.",
      },
      {
        title: "Sponsored Display & DSP",
        desc: "On- and off-Amazon retargeting that recaptures shoppers who viewed but did not buy.",
      },
      {
        title: "Lowest-ACoS Bid Management",
        desc: "Daily bid and placement optimization to keep advertising cost of sale low while scaling spend profitably.",
      },
      {
        title: "Deals, Coupons & Events",
        desc: "Lightning Deals, coupons and Great Indian Festival / Prime Day planning that turn traffic spikes into sales.",
      },
      {
        title: "Full Amazon Ad Management",
        desc: "An all-inclusive program covering structure, creative, negative-keyword hygiene and weekly reporting.",
      },
    ],
    inventory: genericInventory(),
    pricing: genericPricing("Amazon"),
    extraSections: [
      {
        title: "Account Health & Compliance",
        body: "We actively manage your Order Defect Rate, late-shipment rate, policy warnings and A-to-z claims so your account stays in good standing. Suppressed listings, brand-registry issues and category-approval blocks are resolved quickly before they cost you sales.",
      },
      {
        title: "Payment Reconciliation",
        body: "We reconcile every Amazon settlement against your bank deposits, verify referral and FBA fees, flag short payments and raise reimbursement cases for lost or damaged inventory — so the money you have earned actually reaches you.",
      },
    ],
  },

  flipkart: {
    slug: "flipkart",
    name: "Flipkart",
    icon: "store",
    tagline:
      "Flipkart seller account management focused on optimizing your store for maximum sales and visibility — listings, pricing, ads, inventory and account health.",
    hero: `Our Flipkart Seller Account Management Services focus on optimizing your store for maximum sales and visibility. We cover listing optimization, pricing strategy, inventory management, Flipkart SEO, advertising campaigns and review management to increase search rankings, enhance customer engagement and steadily improve your account health and sales performance.`,
    intro: [
      `Flipkart is one of India's largest e-commerce platforms, and selling well on it takes far more than uploading a few products. It takes correct registration, well-optimized listings, sharp pricing, healthy inventory and disciplined advertising — managed consistently.`,
      `${companyName} acts as your dedicated Flipkart account manager, handling the entire spectrum from seller registration and the F-Assured badge to day-to-day operations, so your products appear right at the top of Flipkart search.`,
    ],
    coreServices: [
      {
        title: "Account Setup",
        desc: "We handle the entire Flipkart seller account setup — creation, settings configuration, documentation submission and verification — ensuring you meet every Flipkart requirement.",
      },
      {
        title: "Product Listing Creation & Optimization",
        desc: "Optimized listings with quality images, keyword-rich titles, detailed descriptions and complete specifications, backed by Flipkart SEO for better rankings and discoverability.",
      },
      {
        title: "Store Creation & Design",
        desc: "Professional store design that reflects your brand identity with user-friendly layouts and curated collections to enhance the shopping experience.",
      },
      {
        title: "Optimization for Increased Sales",
        desc: "A comprehensive assessment of store performance covering pricing strategy, conversion-rate optimization and alignment with the Flipkart ranking algorithm.",
      },
      {
        title: "Promotion & Advertising",
        desc: "Management of PCA/PLA ads, Flash Sales and coupon campaigns, with ad spend optimized for maximum ROI and targeted reach.",
      },
      {
        title: "Inventory Management",
        desc: "Monitoring of stock levels and timely replenishment to prevent stockouts or overstocking and maintain operational continuity and profitability.",
      },
    ],
    listingOptimization: genericListingOptimization("Flipkart"),
    advertising: [
      {
        title: "CPC (Cost per Click)",
        desc: "Targeted ads that reach high-intent buyers and only charge when shoppers click.",
      },
      {
        title: "Performance-Driven Ad Management",
        desc: "Ongoing bid optimization and keyword refinement to keep campaigns efficient.",
      },
      {
        title: "Promotion & Campaign Monitoring",
        desc: "Continuous tracking and competitive positioning across your live campaigns.",
      },
      {
        title: "ROI Product Ads",
        desc: "Smart strategies that highlight and scale your best-performing products.",
      },
      {
        title: "Click-Through Optimized Ads",
        desc: "Enhanced creative and copy designed to lift engagement and click-through rate.",
      },
      {
        title: "Sponsored Brands",
        desc: "Brand-level promotional visibility that grows recall and category share.",
      },
      {
        title: "PPC Promotions & Special Campaigns",
        desc: "Peak-season and event promotions built around Big Billion Days and other tentpoles.",
      },
      {
        title: "Complete Flipkart Ad Management",
        desc: "An all-inclusive optimization service covering every campaign type end to end.",
      },
    ],
    inventory: [
      {
        title: "Complete Support",
        desc: "Prevents stockouts, overstocking, storage penalties and fulfilment delays.",
      },
      {
        title: "Expert Fulfilment Operations",
        desc: "Organised storage, accurate tracking and efficient shipping.",
      },
      {
        title: "Real-Time Warehouse Monitoring",
        desc: "Continuous oversight of stock levels across Flipkart facilities.",
      },
      {
        title: "Stock Refill Alerts",
        desc: "Immediate notification and clear guidance on replenishment.",
      },
      {
        title: "Ageing Stock Prevention",
        desc: "Monitoring that avoids holding charges and warehouse fees.",
      },
      {
        title: "Clearance Strategy",
        desc: "Promotional approaches that move slow-moving inventory.",
      },
      {
        title: "Market Research Guidance",
        desc: "Data-driven procurement recommendations on what to stock next.",
      },
    ],
    pricing: [
      {
        title: "Perfect Pricing Support",
        desc: "Competitive yet profitable pricing guidance for every SKU.",
      },
      {
        title: "Know Your Costs",
        desc: "Access to and interpretation of the Flipkart calculator tools.",
      },
      {
        title: "Commission Calculator",
        desc: "Complete clarity on category-wise commission before you price.",
      },
      {
        title: "Category-Based Rates",
        desc: "Understanding the varied rate structures across categories.",
      },
      {
        title: "Flipkart Fee Breakdown",
        desc: "Full transparency on referral, collection, fixed and shipping fees.",
      },
      {
        title: "Multiple Pricing Tools",
        desc: "Access to a range of calculation resources for confident pricing.",
      },
      {
        title: "Profit Through Volume",
        desc: "A strategic, volume-based approach to growing total profit.",
      },
    ],
    extraSections: [
      {
        title: "Flipkart Reconciliation",
        body: "Reconciliation verifies and matches the financial transactions between your Flipkart seller account and your bank account. We compare Flipkart's transaction data against your bank records to identify and resolve discrepancies, ensuring every marketplace payment you are owed is accurate, complete and actually received.",
      },
      {
        title: "Improving Listing Visibility",
        body: "We analyse Flipkart search keywords, apply on-listing optimization techniques, plan deals and promotions to differentiate your products, and actively gather customer ratings and feedback. Together this builds buyer trust and steadily improves your search-ranking positions.",
      },
    ],
  },

  meesho: {
    slug: "meesho",
    name: "Meesho",
    icon: "bag",
    tagline:
      "Meesho seller account management built for India's most price-sensitive, high-volume marketplace — zero-commission selling, sharp catalogues and rating-driven growth.",
    hero: `${companyName} offers complete Meesho seller account management for brands and resellers who want to win on India's fastest-growing value marketplace. From onboarding and catalogue uploads to ads, returns control and rating management, we run your Meesho account so you capture high-volume, price-led demand profitably.`,
    intro: [
      `Meesho's zero-commission model and enormous Tier-2 and Tier-3 reach make it a volume game — but thin margins mean every rupee of price, return and rating matters. Winning here is about a tight catalogue, the right price ladder and a low return-to-origin rate.`,
      `${companyName} manages all of it: we upload and optimize your catalogue, set competitive price points, run Meesho Ads, and keep your quality and rating metrics healthy so your products stay recommended by the algorithm.`,
    ],
    coreServices: genericCoreServices("Meesho"),
    listingOptimization: genericListingOptimization("Meesho"),
    advertising: [
      {
        title: "Meesho Ads (CPC) Setup",
        desc: "Setup and structuring of cost-per-click campaigns to push catalogues into top recommendation slots.",
      },
      {
        title: "Budget & Bid Optimization",
        desc: "Daily budget pacing and bid tuning to capture volume without burning margin.",
      },
      {
        title: "Catalogue-Level Targeting",
        desc: "Identifying which catalogues deserve ad spend and which should stay organic.",
      },
      {
        title: "Conversion-Focused Creatives",
        desc: "Lead images and price points engineered for Meesho's price-comparison shopper.",
      },
      {
        title: "Campaign Monitoring",
        desc: "Continuous tracking of spend, orders and return rate per campaign.",
      },
      {
        title: "Full Meesho Ad Management",
        desc: "End-to-end management of your entire Meesho advertising program.",
      },
    ],
    inventory: genericInventory(),
    pricing: genericPricing("Meesho"),
    extraSections: [
      {
        title: "Return-to-Origin (RTO) Control",
        body: "High RTO and return rates are the silent profit-killers on Meesho. We tighten product descriptions, size charts and images to set correct expectations, monitor return reasons, and remove or rework SKUs that bleed money — protecting both your margin and your quality score.",
      },
      {
        title: "Rating & Quality Management",
        body: "Meesho's algorithm rewards high-rated, low-return catalogues with more visibility. We actively manage your product ratings, next-day-dispatch performance and quality metrics so your best products keep getting recommended to new buyers.",
      },
    ],
  },

  jiomart: {
    slug: "jiomart",
    name: "JioMart",
    icon: "cart",
    tagline:
      "JioMart seller account management across grocery, FMCG, fashion and electronics — onboarding, cataloguing and growth on Reliance's fast-scaling marketplace.",
    hero: `${companyName} provides end-to-end JioMart seller account management for brands looking to tap into Reliance's vast retail and digital ecosystem. We handle onboarding, catalogue creation, pricing, promotions and order operations so you can scale across grocery, FMCG, fashion, home and electronics on JioMart.`,
    intro: [
      `JioMart's strength is its reach into India's everyday shopper through the Reliance ecosystem, but its catalogue and category requirements are exacting. Correct cataloguing, competitive pricing and reliable fulfilment are non-negotiable to rank and stay active.`,
      `${companyName} runs your JioMart account day to day — accurate catalogues, healthy stock, sharp promotions and clean operations — so you grow steadily on one of India's fastest-expanding marketplaces.`,
    ],
    coreServices: genericCoreServices("JioMart"),
    listingOptimization: genericListingOptimization("JioMart"),
    advertising: genericAdvertising("JioMart"),
    inventory: genericInventory(),
    pricing: genericPricing("JioMart"),
    extraSections: [
      {
        title: "Catalogue Approval & Compliance",
        body: "JioMart enforces strict category, attribute and image guidelines. We prepare and submit catalogues that clear approval the first time, fix rejected SKUs quickly, and keep your listings compliant so they stay live and searchable.",
      },
      {
        title: "Order & Fulfilment Operations",
        body: "We manage order processing, dispatch SLAs and returns so your fulfilment metrics stay healthy. Consistent on-time performance keeps your account in good standing and your products eligible for greater visibility.",
      },
    ],
  },

  etsy: {
    slug: "etsy",
    name: "Etsy",
    icon: "etsy",
    tagline:
      "Etsy seller account management for handmade, vintage and craft brands — global SEO, storytelling listings and shop optimization that wins international buyers.",
    hero: `${companyName} helps Indian creators and brands sell to the world on Etsy. From shop setup and policy configuration to keyword-rich listings, photography guidance and Etsy Ads, we manage your Etsy storefront so your handmade, vintage and craft products reach global buyers and convert.`,
    intro: [
      `Etsy is a discovery and storytelling marketplace where strong SEO, beautiful imagery and a trustworthy shop profile decide who gets found. Its global audience is a huge opportunity for Indian artisans and D2C brands — if the shop is set up and optimized correctly.`,
      `${companyName} handles Etsy SEO, listing creation, shop sections, policies and advertising end to end, positioning your products in front of the international shoppers most likely to buy.`,
    ],
    coreServices: [
      {
        title: "Etsy Shop Setup",
        desc: "Complete shop creation — registration, payment and shipping profiles, policies, About section and brand banner — configured to Etsy's best practices.",
      },
      {
        title: "Listing Creation & Etsy SEO",
        desc: "Keyword-researched titles, all 13 tags used strategically, rich descriptions and attributes tuned for Etsy's search algorithm.",
      },
      {
        title: "Shop Design & Storytelling",
        desc: "Curated shop sections, a compelling brand story and cohesive imagery that build trust and encourage multi-item orders.",
      },
      {
        title: "Optimization for Increased Sales",
        desc: "Ongoing review of conversion rate, favourites and search ranking with data-led improvements to listings and pricing.",
      },
      {
        title: "Etsy Ads & Promotions",
        desc: "Etsy Ads management, sales and coupon planning, and Offsite Ads strategy to expand reach profitably.",
      },
      {
        title: "Order & Review Management",
        desc: "Smooth order processing, proactive buyer communication and review generation to grow your shop's reputation.",
      },
    ],
    listingOptimization: genericListingOptimization("Etsy"),
    advertising: [
      {
        title: "Etsy Ads Management",
        desc: "On-platform ad campaigns with daily-budget control and listing-level bidding.",
      },
      {
        title: "Offsite Ads Strategy",
        desc: "Guidance on Etsy's Offsite Ads so you benefit from external reach while protecting margin.",
      },
      {
        title: "Keyword & Tag Optimization",
        desc: "Research-driven titles and all 13 tags aligned to how global buyers actually search.",
      },
      {
        title: "Sales & Coupon Campaigns",
        desc: "Strategically timed discounts and coupons that lift conversion without eroding brand value.",
      },
      {
        title: "Performance Monitoring",
        desc: "Tracking of impressions, click rate and order value to steer spend to winning listings.",
      },
      {
        title: "Full Etsy Ad Management",
        desc: "End-to-end handling of your complete Etsy advertising program.",
      },
    ],
    inventory: genericInventory(),
    pricing: genericPricing("Etsy"),
    extraSections: [
      {
        title: "International Shipping & Profiles",
        body: "Selling globally on Etsy means getting shipping right. We set up accurate shipping profiles, processing times and international rates, advise on packaging and carriers, and configure your policies so cross-border orders run smoothly and reviews stay positive.",
      },
      {
        title: "Brand Story & Trust Building",
        body: "Etsy buyers buy from people and stories, not just products. We craft your About section, shop announcement and policies to build credibility, and manage reviews and messages so your shop earns the trust signals Etsy's algorithm rewards.",
      },
    ],
  },

  ebay: {
    slug: "ebay",
    name: "eBay",
    icon: "ebay",
    tagline:
      "eBay seller account management for cross-border trade — registration, listing optimization, Promoted Listings and best-match ranking on a global marketplace.",
    hero: `${companyName} delivers complete eBay seller account management for businesses selling to buyers across the US, UK, Europe and beyond. We handle account registration, listing creation, Promoted Listings, pricing and feedback management so you grow a profitable global presence on eBay.`,
    intro: [
      `eBay rewards complete, well-categorised listings and strong seller metrics with placement in Best Match search results. For Indian exporters and D2C brands it is one of the most accessible routes into international demand — provided the account is managed to eBay's standards.`,
      `${companyName} manages your eBay account end to end: optimized listings with full item specifics, Promoted Listings campaigns, competitive pricing and disciplined feedback and metric management.`,
    ],
    coreServices: [
      {
        title: "Account Registration & Setup",
        desc: "Complete eBay seller account registration, business policies, payment and shipping configuration to meet eBay's requirements.",
      },
      {
        title: "Listing Creation & Optimization",
        desc: "Keyword-rich titles, complete item specifics, clean galleries and descriptions tuned for eBay's Best Match algorithm.",
      },
      {
        title: "eBay Store Design",
        desc: "A branded eBay Store with organised categories and custom pages that lift trust and repeat purchases.",
      },
      {
        title: "Optimization for Increased Sales",
        desc: "Ongoing tuning of price, item specifics and listing format to improve search ranking and sell-through.",
      },
      {
        title: "Promoted Listings & Ads",
        desc: "Promoted Listings Standard and Advanced campaigns managed for visibility and return on ad spend.",
      },
      {
        title: "Order & Feedback Management",
        desc: "Smooth order handling, returns management and proactive feedback generation to protect Top Rated Seller status.",
      },
    ],
    listingOptimization: genericListingOptimization("eBay"),
    advertising: [
      {
        title: "Promoted Listings Standard",
        desc: "Pay-on-sale promotion that boosts visibility across eBay search and browse.",
      },
      {
        title: "Promoted Listings Advanced",
        desc: "Keyword-targeted, cost-per-click campaigns for premium top-of-search placement.",
      },
      {
        title: "Best-Match Optimization",
        desc: "Listing structure and metric work that improves your organic Best Match ranking.",
      },
      {
        title: "Competitive Repricing",
        desc: "Price positioning informed by sold-listing and competitor data to win the sale.",
      },
      {
        title: "Campaign Monitoring",
        desc: "Continuous tracking of ad rate, sales and ACoS to keep promotion efficient.",
      },
      {
        title: "Full eBay Ad Management",
        desc: "End-to-end management of your complete eBay promotion strategy.",
      },
    ],
    inventory: genericInventory(),
    pricing: genericPricing("eBay"),
    extraSections: [
      {
        title: "Seller Metrics & Top Rated Status",
        body: "eBay ties visibility and protection to your seller metrics. We manage your defect rate, late-shipment rate and case outcomes, handle returns and disputes professionally, and work toward Top Rated Seller status — which unlocks better placement and buyer confidence.",
      },
      {
        title: "Cross-Border Trade Support",
        body: "Selling internationally on eBay means handling global shipping programmes, currency, import expectations and returns. We configure your shipping and return policies for export, advise on carriers and the Global Shipping Programme, and keep international orders running cleanly.",
      },
    ],
  },

  walmart: {
    slug: "walmart",
    name: "Walmart",
    icon: "store",
    tagline:
      "Walmart Marketplace seller account management for the US — approval, catalogue integration, Walmart SEO and Sponsored Search on a fast-growing channel.",
    hero: `${companyName} provides full Walmart Marketplace seller account management for brands targeting US shoppers. From the application and approval process to catalogue integration, content optimization and Walmart Connect advertising, we set up and run your Walmart account for sustainable growth.`,
    intro: [
      `Walmart Marketplace is one of the fastest-growing channels in the US, with less seller saturation than older platforms — a real opportunity for brands that get approved and listed correctly. But onboarding is selective and content standards are strict.`,
      `${companyName} manages the entire journey: the marketplace application, item setup and integration, Walmart SEO, pricing to win the buy box, and Sponsored Search campaigns that scale your sales.`,
    ],
    coreServices: [
      {
        title: "Marketplace Application & Approval",
        desc: "Preparation and submission of your Walmart Marketplace application, with documentation and account setup handled for a smooth approval.",
      },
      {
        title: "Item Setup & Catalogue Integration",
        desc: "Accurate item creation via bulk upload or integration, with complete attributes and category mapping to Walmart's spec.",
      },
      {
        title: "Content & Walmart SEO",
        desc: "Keyword-optimized titles, descriptions and rich media built for Walmart's search and the listing-quality score.",
      },
      {
        title: "Optimization for Increased Sales",
        desc: "Buy-box pricing, content and review work aligned with Walmart's algorithm to lift conversion and rank.",
      },
      {
        title: "Walmart Connect Advertising",
        desc: "Sponsored Search campaign setup, bidding and optimization for visibility and strong return on ad spend.",
      },
      {
        title: "Inventory & WFS Management",
        desc: "Stock monitoring and Walmart Fulfilment Services (WFS) management to keep items in stock and fast-shipping eligible.",
      },
    ],
    listingOptimization: genericListingOptimization("Walmart"),
    advertising: [
      {
        title: "Sponsored Search (CPC)",
        desc: "Keyword-targeted ads placed across Walmart search and browse pages.",
      },
      {
        title: "Bid & Budget Optimization",
        desc: "Continuous bid tuning to keep advertising cost of sale low while scaling.",
      },
      {
        title: "Listing Quality for Ad Eligibility",
        desc: "Content work that meets Walmart's quality bar so your items are ad- and buy-box eligible.",
      },
      {
        title: "ROI-Focused Campaigns",
        desc: "Spend concentrated on best-performing, best-margin items.",
      },
      {
        title: "Campaign Monitoring",
        desc: "Daily tracking of impressions, conversions and ROAS across campaigns.",
      },
      {
        title: "Full Walmart Ad Management",
        desc: "End-to-end handling of your complete Walmart Connect program.",
      },
    ],
    inventory: genericInventory(),
    pricing: [
      {
        title: "Buy Box Pricing Strategy",
        desc: "Competitive pricing that wins and holds the Walmart buy box without sacrificing margin.",
      },
      {
        title: "Know Your Costs",
        desc: "Clarity on referral fees, WFS fees and shipping before you price an item.",
      },
      {
        title: "Category-Based Referral Rates",
        desc: "Mapping Walmart's category-wise referral fees into your pricing.",
      },
      {
        title: "Repricing Support",
        desc: "Rule-based repricing to stay competitive as the market moves.",
      },
      {
        title: "Fee Breakdown Transparency",
        desc: "A complete, itemised view of all marketplace and fulfilment fees.",
      },
      {
        title: "Profit Through Volume",
        desc: "A volume-aware strategy that grows total profit, not just unit margin.",
      },
    ],
    extraSections: [
      {
        title: "Pro Seller Badge & Performance",
        body: "Walmart rewards strong sellers with the Pro Seller Badge and better visibility. We manage your on-time shipping, order defect rate and cancellation rate, keep your listing quality high, and work toward the performance thresholds that earn the badge and buyer trust.",
      },
      {
        title: "WFS & Fulfilment Strategy",
        body: "Walmart Fulfilment Services unlocks fast, free shipping tags that lift conversion. We advise on which SKUs to send to WFS, manage inbound shipments and stock levels, and balance WFS against seller-fulfilled to optimise cost and delivery speed.",
      },
    ],
  },

  alibaba: {
    slug: "alibaba",
    name: "Alibaba",
    icon: "cart",
    tagline:
      "Alibaba.com seller account management for B2B export — Gold Supplier setup, minisite design, product gallery optimization and qualified lead generation.",
    hero: `${companyName} offers complete Alibaba.com account management for Indian manufacturers, wholesalers and exporters who want global B2B buyers. We set up and optimize your Gold Supplier storefront, build a professional minisite, optimize your product gallery and run Keyword Advertising to generate qualified inquiries.`,
    intro: [
      `Alibaba.com is the world's largest B2B marketplace, where buyers place high-value, bulk orders. Success here depends on a credible Gold Supplier presence, a well-built minisite, complete product showcases and a steady flow of qualified leads — a very different game from B2C retail.`,
      `${companyName} manages your Alibaba.com presence end to end: storefront and minisite design, product gallery optimization, Keyword Advertising, and inquiry handling that turns global interest into purchase orders.`,
    ],
    coreServices: [
      {
        title: "Gold Supplier Setup & Verification",
        desc: "Complete account and Gold Supplier onboarding, company-profile creation and verification so global buyers trust you from the first click.",
      },
      {
        title: "Product Gallery Optimization",
        desc: "Keyword-rich product titles, detailed specifications, certifications and high-quality imagery built for Alibaba's B2B search.",
      },
      {
        title: "Minisite Design",
        desc: "A professionally designed minisite that showcases your factory, capabilities and catalogue to build buyer confidence.",
      },
      {
        title: "Optimization for More Inquiries",
        desc: "Ongoing tuning of products and storefront to improve ranking, response rate and the volume of qualified inquiries.",
      },
      {
        title: "Keyword Advertising (P4P)",
        desc: "Pay-for-Performance keyword campaign setup and optimization to put your products in front of active global buyers.",
      },
      {
        title: "Inquiry & RFQ Management",
        desc: "Fast, professional handling of buyer inquiries and Requests for Quotation to convert interest into orders.",
      },
    ],
    listingOptimization: genericListingOptimization("Alibaba"),
    advertising: [
      {
        title: "Keyword Advertising (P4P) ",
        desc: "Pay-for-Performance campaigns that place your products against high-intent B2B searches.",
      },
      {
        title: "Bid & Keyword Optimization",
        desc: "Continuous keyword and bid refinement to maximise qualified inquiries per rupee.",
      },
      {
        title: "Top-Ranking Products Strategy",
        desc: "Identifying and pushing the products most likely to attract serious buyers.",
      },
      {
        title: "Showcase & Premium Placements",
        desc: "Strategic use of showcase slots and premium placements for visibility.",
      },
      {
        title: "Performance Monitoring",
        desc: "Tracking of impressions, clicks and inquiry quality to steer spend.",
      },
      {
        title: "Full Alibaba Ad Management",
        desc: "End-to-end management of your complete Alibaba.com advertising program.",
      },
    ],
    inventory: genericInventory(),
    pricing: genericPricing("Alibaba"),
    extraSections: [
      {
        title: "Lead Generation & Buyer Trust",
        body: "On Alibaba.com, credibility drives inquiries. We complete your verified company profile, certifications and trade assurance, optimise response time and showcase capability so buyers feel confident placing large orders. The result is a steady stream of qualified, export-ready leads.",
      },
      {
        title: "Find Best Suppliers & Sourcing Support",
        body: "Beyond selling, we help businesses source on Alibaba.com — identifying reliable suppliers, vetting them, comparing quotations and guiding AliExpress dropshipping setups — so your procurement is as strong as your sales presence.",
      },
    ],
  },
};

// Stable, ordered list used by the hub grid and the subpage "other marketplaces"
// section so links and pages always stay in sync (no dead links, no blank pages).
export const marketplaceOrder = [
  "amazon",
  "flipkart",
  "meesho",
  "jiomart",
  "etsy",
  "ebay",
  "walmart",
  "alibaba",
];

export const marketplaceList = marketplaceOrder.map((slug) => marketplaces[slug]);
